import { useCallback, useEffect, useRef, useState } from "react";
import { Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, fieldInputCls } from "@/components/Field";
import { TagInput } from "@/components/TagInput";
import { LocationPicker } from "@/components/LocationPicker";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", city: "", country: "", lat: null, lon: null, length_m: null, aliases: [], notes: "" };
const num = (v) => (v === "" || v == null ? null : Number(v));

export const TrackDialog = ({ open, onOpenChange, item, onSaved }) => {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    if (open) {
      setForm(item ? { ...EMPTY, ...item } : EMPTY);
      setLink("");
      prev.current = {};
    }
  }, [open, item]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLatLon = useCallback((lat, lon) => setForm((f) => ({ ...f, lat, lon })), []);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const prev = useRef({});
  const importLink = async () => {
    if (!link.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tracks/from-link", { url: link });
      const keep = (f, k) => (f[k] && f[k] !== prev.current[k] ? f[k] : data[k]);
      setForm((f) => ({ ...f, name: keep(f, "name"), city: data.city || keep(f, "city"), country: data.country || keep(f, "country"), lat: data.lat, lon: data.lon }));
      prev.current = data;
      toast.success(t("gmaps_done"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { name: form.name, city: form.city, country: form.country, lat: num(form.lat), lon: num(form.lon),
      length_m: num(form.length_m), aliases: form.aliases, notes: form.notes };
    try {
      if (item) await api.put(`/tracks/${item.id}`, payload);
      else await api.post("/tracks", payload);
      toast.success(t("saved"));
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="track-dialog" className="bg-[#12141C] border-[#282D3D] rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl">{item ? t("edit_track") : t("new_track")}</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">{t("track_dialog_hint")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div data-testid="gmaps-import" className="sm:col-span-2 border border-[#3B82F6]/40 bg-[#3B82F6]/5 rounded-sm p-3 space-y-2">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#93C5FD]"><Link2 className="w-3.5 h-3.5" /> {t("gmaps_link")}</div>
            <div className="flex gap-2">
              <Input data-testid="gmaps-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://maps.app.goo.gl/…  ·  40.1288, 18.3504"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); importLink(); } }} className={fieldInputCls} />
              <Button type="button" data-testid="gmaps-import-btn" disabled={busy || !link.trim()} onClick={importLink}
                className="rounded-sm bg-[#3B82F6] hover:bg-[#2f6fe0] font-heading uppercase tracking-wide shrink-0">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("gmaps_import")}
              </Button>
            </div>
            <p className="font-mono text-[10px] text-slate-500">{t("gmaps_hint")}</p>
          </div>
          <Field label={`${t("name")} *`} className="sm:col-span-2">
            <Input data-testid="track-name-input" required value={form.name} onChange={set("name")} className={fieldInputCls} />
          </Field>
          <Field label={t("city")}><Input data-testid="track-city-input" value={form.city} onChange={set("city")} className={fieldInputCls} /></Field>
          <Field label={t("country")}><Input data-testid="track-country-input" value={form.country} onChange={set("country")} className={fieldInputCls} /></Field>
          <Field label="Lat"><Input data-testid="track-lat-input" type="number" step="any" value={form.lat ?? ""} onChange={set("lat")} className={fieldInputCls} /></Field>
          <Field label="Lon"><Input data-testid="track-lon-input" type="number" step="any" value={form.lon ?? ""} onChange={set("lon")} className={fieldInputCls} /></Field>
          <div className="sm:col-span-2">{open && <LocationPicker lat={num(form.lat)} lon={num(form.lon)} onChange={setLatLon} />}</div>
          <Field label={t("track_length")}><Input data-testid="track-length-input" type="number" min="0" value={form.length_m ?? ""} onChange={set("length_m")} className={fieldInputCls} /></Field>
          <Field label={t("aliases")}><TagInput testid="track-aliases" values={form.aliases} onChange={(v) => setForm({ ...form, aliases: v })} /></Field>
          <Field label={t("notes")} className="sm:col-span-2">
            <Textarea data-testid="track-notes-input" rows={2} value={form.notes} onChange={set("notes")} className={`${fieldInputCls} h-auto font-sans`} />
          </Field>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" data-testid="track-cancel-btn" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button type="submit" data-testid="track-save-btn" className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base px-6">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
